import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Phone, Users, ShoppingBag, Wallet, AlertCircle, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../layouts/AdminLayout';
import { customersApi } from '../../services/api';
import { formatCurrency, formatDate, StatusBadge, PaymentBadge } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import WaEmulator from '../../components/WaEmulator';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [message, setMessage] = useState('');
  const [sentMessage, setSentMessage] = useState('');
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [startDate, setStartDate] = useState(() => new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10));

  const { data, isLoading } = useQuery({
    queryKey: ['customer-stats', id, startDate, endDate],
    queryFn: () => customersApi.getStats(id, { start: startDate, end: endDate }).then(r => r.data),
    enabled: !!id,
  });

  const sendMutation = useMutation({
    mutationFn: () => customersApi.sendWa(id, message),
    onSuccess: () => {
      toast.success('Pesan WA berhasil dikirim!');
      setSentMessage(message);
      setMessage('');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Gagal mengirim WA'),
  });
  const preferenceMutation = useMutation({
    mutationFn: whatsapp_opt_out => customersApi.update(id, { whatsapp_opt_out }),
    onSuccess: () => {
      toast.success('Preferensi notifikasi WhatsApp disimpan');
      queryClient.invalidateQueries({ queryKey: ['customer-stats', id] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: err => toast.error(err?.response?.data?.detail || 'Gagal menyimpan preferensi WhatsApp'),
  });

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!data) return null;

  const { customer, stats, orders } = data;
  const whatsappDisabled = Boolean(customer.whatsapp_opt_out);
  const storeName = customer.store_name || 'Toko';

  const statCards = [
    { label: t('Total Pesanan', 'Total Orders'), value: stats.total_pesanan, icon: ShoppingBag, color: 'bg-blue-50 text-blue-600' },
    { label: t('Total Transaksi', 'Total Revenue'), value: formatCurrency(stats.total_transaksi), icon: Wallet, color: 'bg-purple-50 text-purple-600' },
    { label: t('Sudah Dibayar', 'Paid'), value: formatCurrency(stats.sudah_dibayar), icon: Wallet, color: 'bg-green-50 text-green-600' },
    { label: t('Belum Dibayar', 'Outstanding'), value: formatCurrency(stats.belum_dibayar), icon: AlertCircle, color: 'bg-red-50 text-red-500' },
  ];

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Back + Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
            <ArrowLeft size={18} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-manrope">{customer.name}</h1>
            <div className="flex items-center gap-2 text-sm text-slate-500 mt-0.5">
              {customer.phone && <span className="flex items-center gap-1"><Phone size={12} />{customer.phone}</span>}
              {customer.email && <span>{customer.email}</span>}
            </div>
            {whatsappDisabled && <span data-testid="customer-detail-wa-disabled-badge" className="mt-2 inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">WA Nonaktif</span>}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Periode Transaksi</label><div className="flex flex-wrap items-center gap-2"><input type="date" value={startDate} onChange={event => setStartDate(event.target.value)} data-testid="customer-detail-date-start" className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-[#0f6c44]"/><span className="text-sm text-slate-400">s/d</span><input type="date" value={endDate} onChange={event => setEndDate(event.target.value)} data-testid="customer-detail-date-end" className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 outline-none focus:border-[#0f6c44]"/></div></div>
          <button onClick={() => { setStartDate(new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10)); setEndDate(new Date().toISOString().slice(0, 10)); }} data-testid="customer-detail-last-30-days-button" className="rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-[#0f6c44] hover:bg-emerald-100">30 Hari Terakhir</button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {statCards.map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${color}`}>
                <Icon size={17} />
              </div>
              <p className="text-xs text-slate-400 font-medium">{label}</p>
              <p className="text-lg font-bold text-slate-900 font-manrope mt-0.5">{value}</p>
            </div>
          ))}
        </div>

        <div data-testid="customer-transaction-chart" className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-5"><h2 className="font-manrope font-bold text-slate-800">Aktivitas Transaksi</h2><p className="mt-1 text-xs text-slate-400">Jumlah transaksi dan nominal pada periode yang dipilih.</p></div>
          <div className="h-64 w-full"><ResponsiveContainer width="100%" height="100%"><AreaChart data={data.chart_data || []} margin={{top:10,right:8,left:0,bottom:0}}><defs><linearGradient id="customer-amount-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0f6c44" stopOpacity={0.3}/><stop offset="95%" stopColor="#0f6c44" stopOpacity={0}/></linearGradient></defs><CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false}/><XAxis dataKey="date" tick={{fill:'#64748b',fontSize:11}} tickLine={false} axisLine={false} minTickGap={24}/><YAxis yAxisId="amount" tickFormatter={value=>`${Math.round(value/1000)}k`} tick={{fill:'#64748b',fontSize:11}} tickLine={false} axisLine={false} width={42}/><YAxis yAxisId="count" orientation="right" allowDecimals={false} tick={{fill:'#64748b',fontSize:11}} tickLine={false} axisLine={false} width={28}/><Tooltip labelFormatter={label=>`Tanggal ${label}`} formatter={(value,name)=>[name==='amount'?formatCurrency(value):value,name==='amount'?'Total Nominal':'Jumlah Transaksi']} contentStyle={{borderRadius:12,border:'1px solid #e2e8f0',fontSize:12}}/><Legend formatter={value=>value==='amount'?'Total Nominal':'Jumlah Transaksi'}/><Area yAxisId="amount" type="monotone" dataKey="amount" stroke="#0f6c44" strokeWidth={2.5} fill="url(#customer-amount-fill)" activeDot={{r:5}}/><Area yAxisId="count" type="monotone" dataKey="transactions" stroke="#2563eb" strokeWidth={2} fill="transparent" activeDot={{r:4}}/></AreaChart></ResponsiveContainer></div>
        </div>

        {/* Main grid: Orders (left) + Send WA (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Order List */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-50">
              <ShoppingBag size={15} className="text-slate-400" />
              <h3 className="font-semibold text-slate-800 text-sm font-manrope">{t('Riwayat Pesanan', 'Order History')}</h3>
              <span className="ml-auto text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{orders.length}</span>
            </div>
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag size={28} className="text-slate-200 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">{t('Belum ada pesanan', 'No orders yet')}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {orders.map(order => (
                  <button key={order.id} onClick={() => navigate(`/admin/orders/${order.id}`)}
                    className="w-full text-left px-5 py-3.5 hover:bg-slate-50/70 transition-colors flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800 text-sm font-manrope">{order.tracking_code}</span>
                        <StatusBadge status={order.status} />
                        <PaymentBadge status={order.payment_status} />
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{formatDate(order.created_at)}</p>
                    </div>
                    <p className="font-bold text-slate-800 text-sm flex-shrink-0">{formatCurrency(order.total_amount)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Send WA */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <label className="flex cursor-pointer items-start gap-3">
                <input type="checkbox" checked={!whatsappDisabled} disabled={preferenceMutation.isPending} onChange={event => preferenceMutation.mutate(!event.target.checked)} data-testid="customer-detail-whatsapp-notifications-toggle" className="mt-0.5 h-4 w-4 accent-[#0f6c44] disabled:cursor-not-allowed" />
                <span><span className="block text-sm font-semibold text-slate-800">Kirim notifikasi WhatsApp</span><span className="mt-1 block text-xs text-slate-500">Pesan status, pembayaran, reminder, pesan manual, dan broadcast akan dihentikan saat dinonaktifkan.</span></span>
              </label>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4">
              <div className="flex items-center gap-2">
                <MessageCircle size={16} className="text-[#25D366]" />
                <h3 className="font-semibold text-slate-800 text-sm font-manrope">{t('Kirim WA ke Pelanggan', 'Send WhatsApp')}</h3>
              </div>
              {customer.phone && !whatsappDisabled ? (
                <>
                  <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
                    <Phone size={13} className="text-slate-400 flex-shrink-0" />
                    <span className="text-sm text-slate-700 font-medium">{customer.phone}</span>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1.5">{t('Isi Pesan', 'Message')}</label>
                    <textarea
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      placeholder={t('Tulis pesan WA di sini...', 'Write your WhatsApp message here...')}
                      rows={5}
                      data-testid="wa-message-textarea"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44] bg-slate-50 resize-none"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      *teks* = <strong>bold</strong> · _teks_ = <em>italic</em>
                    </p>
                  </div>
                  <button
                    onClick={() => sendMutation.mutate()}
                    disabled={sendMutation.isPending || !message.trim()}
                    data-testid="send-wa-btn"
                    className="w-full py-2.5 bg-[#25D366] text-white rounded-xl text-sm font-semibold hover:bg-[#1ebe57] active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                    {sendMutation.isPending
                      ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <Send size={15} />}
                    {t('Kirim via Fonnte', 'Send via Fonnte')}
                  </button>
                </>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                  {whatsappDisabled ? 'Pesan manual dinonaktifkan karena pelanggan memilih tidak menerima notifikasi WhatsApp.' : t('Pelanggan tidak memiliki nomor HP terdaftar.', 'Customer has no phone number.')}
                </div>
              )}
            </div>

            {/* WA Preview */}
            <WaEmulator
              message={message || sentMessage}
              senderName={customer.name}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
